/**
 * Render layer and map-level features on a grid and map and manage viewport changes
 * @module render-features
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/render-features|blog}
 */

import _ = require("underscore");
import ol = require("ol");
import Mn = require("backbone.marionette");
import MapView = require("../views/MapView");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Mediator = require("../mediator");
import Base = require("./base-command");
import defaults = require("../models/defaults");
import Converter = require("../utils/ol3/converter");
import geo = require("../utils/geo");
import Controller = require("../controls/controller");

class Command extends Base<{
    in: any, // todo, describe the possibilities in an public interface
    out: {
        // why not a portal layer?
        layers: Array<Rhythm.GIS.FeatureQueryResultLayer>;
        zoommap?: boolean;
        panmap?: boolean;
    }
}> {

    static about = `
Render layer and map-level features on a grid and map and manage viewport changes.

Usage Example:

\`\`\`xml
<EVENT about="Add features on the map" event="show-in-map" id="show-in-map-handler" mid="app/commands/render-features">
  <OPTIONS>
    <Values id="grid" value="false" />
    <Values id="map" value="true" />
    <Values id="preserve-extent" value="false" />
  </OPTIONS>
</EVENT>
\`\`\`

See https://wiki.infor.com/confluence/display/~CALIX/2015/10/13/render-features

    `;

    static options = {
        map: {
            "about": "indicate if the results should be rendered on the map (requires symbology)",
            "domain": "true|false",
            "default": "true",
        },
        panmap: {
            "about": "indicate if the map should pan to show the result(s)",
            "domain": "true|false",
            "default": "true",
        },
        symbology: {
            "about": "symbology to use",
            "default": "*",
        },
        zoommap: {
            "about": "indicate if the map should zoom in/out to show the result(s)",
            "domain": "true|false",
            "default": "true",
        },
    };

    static output = {
        "layers": {
            "about": "features to be rendered",
        },
    }

    execute() {
        // TODO: should not be overloaded to render grid, markers and update viewport, split into three commands
        if (!this.args.out) {
            console.warn("this should only be called as an event handler, not a startup command");
            return;
        }
        console.log("adding configuration features to feature layer");

        let config = this.asOptions(Command.options);

        if (config.map !== "false") {
            // allow args to take precedence but honor this commands configuration
            ["panmap", "zoommap"].forEach(key => {
                (typeof this.args.out[key] === "undefined") &&
                    Utils.ifOption(config, key, v => this.args.out[key] = v === "true");
            });

            let layers = this.renderFeaturesOnMap();
            // I'm so confused by what I did with symbology...these layers are the configuration layers with features that have
            // options that specify the symbology so anything listening to this command will be able to access them in args.out.layers
            this.trigger({ layers: layers });
        }

    }

    private getId() {
        return this.configuration.id;
    }

    /**
     * convert to configured features
    */
    formatLayers() {
        let converter = new Converter();
        let inputLayers = <Rhythm.GIS.FeatureQueryResultLayer[]>this.findOption("layers");
        if (!inputLayers) {
            console.warn("no layers to render");
            return [];
        }

        let defaultSymbology = this.findOption("symbology");

        let layers = inputLayers.map(layer => {
            let symbology = Utils.extend(layer).symbology;
            if (!symbology && defaultSymbology) {
                symbology = defaultSymbology;
                Utils.extend(layer, { symbology: symbology });
            }
            if (symbology) {
                // push symbology into features that do not already have a symbology
                // since the containing layer gets dropped due to merging features at the maplet level
                layer.features.forEach(f => _.defaults(Utils.extend(f), {
                    symbology: symbology
                }));
            }

            return converter.toConfigLayer(layer);
        });

        return layers;
    }

    // TODO: should be its own command
    private renderFeaturesOnMap() {
        let layers = this.formatLayers();

        layers.forEach(layer => {

            let appExt: Portal.MapApplicationElement = {
                Map: {
                    Features: layer.Features
                }
            };

            this.mediator.api.merge(appExt);


            return appExt.Map.Features;
        });

        return layers;
    }

}

export = Command;
